import * as React from "react";
import { useState } from "react";
import {
  Text,
  Button,
  RadioGroup,
  Radio,
  Label,
  Checkbox,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Signature24Regular } from "@fluentui/react-icons";
import { DiscussionDto } from "../../../services/discussions.service";
import { StatusItemDto } from "../../../services/status-items.service";
import { tryCreateCompletionCertificate } from "../completionCertificate";
import {
  signAsPartner,
  signAsSecondaryPartner,
  SpreadsheetReviewInfoResponse,
  SignRequest,
} from "../../../services/spreadsheets.service";
import { UserRole } from "../../../types/UserRole";
import { ExcelService } from "../../../services/excel.service";
import { isStandaloneMode } from "../../../utils/standalone-mode";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useTypographyStyles } from "../../../styles/typography.styles";
import LiveReviewPoints from "./LiveReviewPoints";
import ReviewPointsSnapshot from "./ReviewPointsSnapshot";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

const PARTNER_CHECKLIST_ITEMS = [
  "Clear all significant matters noted for partner attention",
  "Confirm that all discussions with the client concerning significant matters have been adequately reflected in the final accounts, with decisions taken evidenced on the file.",
];

const PARTNER_CONFIRMATION_HEADER =
  "Suitable enquiry has been made of the client as to:";

const PARTNER_CONFIRMATION_ITEMS = [
  "Substantial commitments or uncertainties arising since Balance Date",
  "Significant changes in the capital, long term debt or working capital",
  "Any other changes which affect the “going concern” assumption",
];

const PARTNER_CONFIRMATION_FOOTER =
  "Due consideration has been given to these matters and if appropriate, a file note pertaining to the financial reports/client meeting has been created and saved.";

const useStyles = makeStyles({
  tabPanel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  checklistItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: tokens.spacingVerticalXS,
  },
  /** Used on the form-label wrapper so long question text wraps onto multiple lines. */
  formLabelWrap: {
    whiteSpace: "pre-line",
  },
  signButtonRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
});

interface PartnerTabProps {
  spreadsheetId: string | null;
  userRole: UserRole;
  reviewInfo: SpreadsheetReviewInfoResponse | null;
  discussions: DiscussionDto[];
  statusItems: StatusItemDto[];
  /**
   * Why the workbook can't be written to (read-only / never saved), or
   * null when it's writable. Partner sign-off writes the completion
   * certificate into the workbook AND is the terminal state, so a
   * non-null value blocks both sign buttons.
   */
  workbookBlockReason: string | null;
  onReviewInfoUpdated: (info: SpreadsheetReviewInfoResponse) => void;
}

/**
 * Partner sign-off tab: shows the manager's recap (checklist + snapshot) for
 * context, then the partner's own checklist, confirmations, and sign buttons.
 * Edits are gated to the Partner role; pre-manager-sign the controls show but
 * are disabled with a guidance message at the top.
 */
const PartnerTab: React.FC<PartnerTabProps> = ({
  spreadsheetId,
  userRole,
  reviewInfo,
  discussions,
  statusItems,
  workbookBlockReason,
  onReviewInfoUpdated,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const track = useTrackEvent();

  const [partnerResponses, setPartnerResponses] = useState<string[]>(
    new Array(PARTNER_CHECKLIST_ITEMS.length).fill("")
  );
  const [partnerConfirmations, setPartnerConfirmations] = useState<boolean[]>(
    new Array(PARTNER_CONFIRMATION_ITEMS.length).fill(false)
  );
  const [isSigning, setIsSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allPartnerAnswered = partnerResponses.every((r) => r === "yes" || r === "no");
  const allPartnerConfirmed = partnerConfirmations.every(Boolean);
  const partnerAlreadySigned = !!reviewInfo?.signingPartnerEmail;
  const secondaryPartnerAlreadySigned = !!reviewInfo?.secondarySigningPartnerEmail;
  const canEditPartner = userRole === UserRole.Partner;
  // Partner sign generates the Excel completion certificate — impossible
  // outside Excel — so the sign buttons are blocked in standalone. Manager
  // sign is unaffected (no Excel dependency).
  const isStandalone = isStandaloneMode();

  const handlePartnerResponseChange = (index: number, value: string) => {
    setPartnerResponses((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handlePartnerConfirmationChange = (index: number, checked: boolean) => {
    setPartnerConfirmations((prev) => {
      const next = [...prev];
      next[index] = checked;
      return next;
    });
  };

  // Both sign endpoints call EnsureCallerIsPartnerAsync server-side, so a
  // 403 here means exactly one thing: the caller isn't a partner. The
  // frontend role gate normally prevents them getting this far, but the
  // two sources disagree — the gate reads the locally-stored role, while
  // the backend reads Digital Hub group membership — so a mismatch lands
  // the user on a 403 with the generic "cannot access this resource",
  // which gives them nothing to act on.
  const PARTNER_FORBIDDEN_MESSAGE =
    "You do not have permission to sign off as a partner. This is restricted to " +
    "users with the Partner role in Digital Hub.";

  const handlePartnerSign = async () => {
    if (!spreadsheetId || !allPartnerAnswered || !allPartnerConfirmed || partnerAlreadySigned) return;
    if (workbookBlockReason) return;

    setIsSigning(true);
    setError(null);
    try {
      // Re-check immediately before committing. The mount-time probe
      // drives the disabled state, but the workbook can flip to
      // read-only while the page is open (someone else opens the file).
      // Partner sign is the workbook's terminal state and can't be
      // undone, so verify before committing rather than after.
      await ExcelService.assertWorkbookWritable("the sign-off");

      const request: SignRequest = {
        checklistResponses: PARTNER_CHECKLIST_ITEMS.map((text, index) => ({
          index,
          text,
          response: partnerResponses[index] as "yes" | "no",
        })),
      };
      const updatedInfo = await signAsPartner(spreadsheetId, request);
      track("signoff.partner_signed");
      onReviewInfoUpdated(updatedInfo);
      // Never throws — returns a message when the certificate couldn't be
      // written (read-only workbook, protected sheet, save failure). The
      // partner sign-off has already committed server-side at this point,
      // so we surface the message rather than failing the sign.
      const certWarning = await tryCreateCompletionCertificate(updatedInfo, spreadsheetId, statusItems);
      if (certWarning) {
        setError(certWarning);
      } else {
        track("completion_certificate.generated");
      }
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err, { forbidden: PARTNER_FORBIDDEN_MESSAGE }));
    } finally {
      setIsSigning(false);
    }
  };

  const handleSecondaryPartnerSign = async () => {
    if (!spreadsheetId || !partnerAlreadySigned || secondaryPartnerAlreadySigned) return;
    if (workbookBlockReason) return;

    setIsSigning(true);
    setError(null);
    try {
      // Secondary sign rewrites the certificate too, so it needs the
      // same guard as the primary partner sign.
      await ExcelService.assertWorkbookWritable("the sign-off");

      const updatedInfo = await signAsSecondaryPartner(spreadsheetId);
      onReviewInfoUpdated(updatedInfo);
      const certWarning = await tryCreateCompletionCertificate(updatedInfo, spreadsheetId, statusItems);
      if (certWarning) {
        setError(certWarning);
      } else {
        track("completion_certificate.generated");
      }
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err, { forbidden: PARTNER_FORBIDDEN_MESSAGE }));
    } finally {
      setIsSigning(false);
    }
  };

  // Partners can sign without waiting for manager — there are jobs where the
  // partner signs off without a manager review step.
  const partnerControlsDisabled = !canEditPartner;

  return (
    <div className={styles.tabPanel}>
      <div className={styles.section}>
        <Text className={typography.subsectionTitle}>Pre-signing Checklist</Text>
        {partnerAlreadySigned && reviewInfo?.partnerChecklistResponses ? (
          // Show stored responses readonly after signing
          reviewInfo.partnerChecklistResponses.items.map((item, index) => (
            <div key={index} className={styles.checklistItem}>
              <Label className={`${typography.formLabel} ${styles.formLabelWrap}`}>{item.text}</Label>
              <Text
                className={item.response === "yes" ? typography.responseYes : typography.responseNo}
              >
                {item.response === "yes" ? "Yes" : "No"}
              </Text>
            </div>
          ))
        ) : (
          // Interactive radio buttons before signing
          PARTNER_CHECKLIST_ITEMS.map((item, index) => (
            <div key={index} className={styles.checklistItem}>
              <Label className={`${typography.formLabel} ${styles.formLabelWrap}`}>{item}</Label>
              <RadioGroup
                layout="horizontal"
                value={partnerResponses[index]}
                onChange={(_, data) => handlePartnerResponseChange(index, data.value)}
                disabled={partnerControlsDisabled || (partnerAlreadySigned && secondaryPartnerAlreadySigned)}
              >
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No" />
              </RadioGroup>
            </div>
          ))
        )}
      </div>

      <div className={styles.section}>
        <Text className={`${typography.bodyLarge} ${styles.formLabelWrap}`}>
          My review of the accounts and workpapers is completed subject to the following outstanding matters:
        </Text>
        {/* Partner's snapshot only exists after partner signs. Otherwise show live list. */}
        {partnerAlreadySigned && reviewInfo?.partnerReviewPointsSnapshot ? (
          <ReviewPointsSnapshot
            snapshot={reviewInfo.partnerReviewPointsSnapshot}
            title="Review Points at Partner Sign-off"
          />
        ) : (
          <LiveReviewPoints discussions={discussions} />
        )}
      </div>

      <div className={styles.section}>
        <Text className={typography.subsectionTitle}>{PARTNER_CONFIRMATION_HEADER}</Text>
        {PARTNER_CONFIRMATION_ITEMS.map((item, index) => (
          <Checkbox
            key={index}
            label={item}
            checked={partnerAlreadySigned || partnerConfirmations[index]}
            disabled={partnerControlsDisabled || partnerAlreadySigned}
            onChange={(_, data) =>
              handlePartnerConfirmationChange(index, !!data.checked)
            }
          />
        ))}
        <Text className={`${typography.bodyLarge} ${styles.formLabelWrap}`}>
          {PARTNER_CONFIRMATION_FOOTER}
        </Text>
      </div>

      {(!partnerAlreadySigned || !secondaryPartnerAlreadySigned) && workbookBlockReason && (
        <Text className={typography.errorText}>{workbookBlockReason}</Text>
      )}

      {error && (
        <Text className={typography.errorText}>{error}</Text>
      )}

      {isStandalone && (!partnerAlreadySigned || !secondaryPartnerAlreadySigned) && (
        <Text className={typography.errorText}>
          Partner sign-off isn't available in this browser tab — it writes a completion certificate directly into the Excel workbook. Open Workflow Intelligence inside Excel to sign off.
        </Text>
      )}

      <div className={styles.signButtonRow}>
        {!partnerAlreadySigned && (
          <Button
            appearance="primary"
            icon={<Signature24Regular />}
            disabled={
              partnerControlsDisabled ||
              !allPartnerAnswered ||
              !allPartnerConfirmed ||
              isSigning ||
              isStandalone ||
              !!workbookBlockReason
            }
            onClick={handlePartnerSign}
            size="medium"
          >
            {isSigning ? "Signing..." : "Partner Sign"}
          </Button>
        )}
        {!secondaryPartnerAlreadySigned && (
          <Button
            appearance="primary"
            icon={<Signature24Regular />}
            // Secondary sign is a co-signature — no checklist/confirmations
            // of its own (backend doesn't record any). Just requires the
            // primary partner has already signed, matching the intended
            // workflow. The checklist gate would otherwise strand any
            // partner arriving fresh to secondary-sign, since the radios
            // become readonly text once primary signs.
            disabled={
              partnerControlsDisabled ||
              !partnerAlreadySigned ||
              isSigning ||
              isStandalone ||
              !!workbookBlockReason
            }
            onClick={handleSecondaryPartnerSign}
            size="medium"
          >
            {isSigning ? "Signing..." : "Secondary Partner Sign"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PartnerTab;
